import classNames from 'classnames'
import { type FC, type ReactNode, useState } from 'react'
import { Col, Container, Row, Tab } from 'react-bootstrap'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { LinkButton } from '../../components/Button/LinkButton'
import { Spinner } from '../../components/Spinner/Spinner'
import { Tabs } from '../../components/Tabs/Tabs'
import { useDocumentTitle } from '../../common/useDocumentTitle'
import { type SchedulingFilters } from '../../models/Scheduling'
import { selectSafeAuthenticatedUser } from '../../store/auth/authSlice'
import { useAppSelector } from '../../store/hooks'
import { SCHEDULING_TAB_KEYS, type SchedulingTabKey } from './bookingTabs'
import { SchedulingFilterBar } from './FilterBar/SchedulingFilterBar'
import { SchedulingBlockedState } from './SchedulingBlockedState'
import { RequestsTab } from './tabs/RequestsTab'
import { ScheduledTab } from './tabs/ScheduledTab'
import { SuggestedTab } from './tabs/SuggestedTab'
import { WaitingTab } from './tabs/WaitingTab'
import { useSchedulingPageStatus } from './useSchedulingPageStatus'

interface SchedulingTabDefinition {
  key: SchedulingTabKey
  title: string
  subtitle: string
  hasAlertBadge: boolean
  content: (filters: SchedulingFilters) => ReactNode
}

const TAB_DEFINITIONS: SchedulingTabDefinition[] = [
  {
    key: 'requests',
    title: 'Requests',
    subtitle: 'Client requests waiting for your response.',
    hasAlertBadge: true,
    content: (filters) => <RequestsTab filters={filters} />,
  },
  {
    key: 'suggested',
    title: 'Suggested',
    subtitle: 'Meeting opportunities detected by your AI Assistant.',
    hasAlertBadge: false,
    content: (filters) => <SuggestedTab filters={filters} />,
  },
  {
    key: 'waiting',
    title: 'Waiting',
    subtitle: 'Waiting for clients to pick a time. Send a reminder to follow up.',
    hasAlertBadge: false,
    content: (filters) => <WaitingTab filters={filters} />,
  },
  {
    key: 'scheduled',
    title: 'Scheduled',
    subtitle: 'Calendar invites sent. Open meeting prep or reschedule.',
    hasAlertBadge: false,
    content: (filters) => <ScheduledTab filters={filters} />,
  },
]

const TabTitle: FC<{ definition: SchedulingTabDefinition; count: number }> = ({ definition, count }) => (
  <div className='d-flex align-items-center zocks-gap-0.5'>
    <span className='text-uppercase'>{definition.title}</span>
    {count > 0 ? (
      <span className={classNames('badge rounded-pill', definition.hasAlertBadge ? 'bg-danger text-white' : 'zocks-bg-color-gray-200 zocks-text-gray-600')}>
        {count}
      </span>
    ) : null}
  </div>
)

export const SchedulingPage: FC = () => {
  const { activeTab = 'requests' } = useParams()
  const navigate = useNavigate()
  const currentUser = useAppSelector(selectSafeAuthenticatedUser)
  const [filters, setFilters] = useState<SchedulingFilters>(() => ({ userIds: [currentUser.id] }))
  const { status, refetch, counts, eventTypes } = useSchedulingPageStatus(filters)
  const activeTabTitle = TAB_DEFINITIONS.find((definition) => definition.key === activeTab)?.title ?? 'Requests'
  useDocumentTitle(`Scheduling - ${activeTabTitle} | Zocks`)

  if (!SCHEDULING_TAB_KEYS.includes(activeTab as SchedulingTabKey)) {
    return <Navigate to='/scheduling/requests' replace />
  }

  if (status.kind === 'loading') {
    return (
      <Container>
        <Row className='mb-9'>
          <Col>
            <h3 className='mb-0'>Scheduling</h3>
          </Col>
        </Row>
        <div className='d-flex justify-content-center py-5'>
          <Spinner />
        </div>
      </Container>
    )
  }

  if (status.kind !== 'ready') {
    return (
      <Container>
        <Row className='mb-9'>
          <Col>
            <h3 className='mb-0'>Scheduling</h3>
          </Col>
        </Row>
        <Row>
          <Col>
            <SchedulingBlockedState status={status} onRefresh={refetch} />
          </Col>
        </Row>
      </Container>
    )
  }

  const changeTab = (key: string | null) => {
    if (key) {
      navigate(`/scheduling/${key}`, { replace: true })
    }
  }

  return (
    <Container>
      <Row className='mb-9'>
        <Col className='d-flex align-items-center justify-content-between'>
          <h3 className='mb-0'>Scheduling</h3>
          <LinkButton variant='primary' to='/scheduling/schedule'>
            <i className='bi bi-plus-lg me-1' />
            Schedule a meeting
          </LinkButton>
        </Col>
      </Row>
      <Row className='mb-4'>
        <Col>
          <SchedulingFilterBar eventTypes={eventTypes ?? []} filters={filters} onFiltersChange={setFilters} />
        </Col>
      </Row>
      <Row>
        <Col>
          <Tabs activeKey={activeTab} onSelect={changeTab} className='mb-4' mountOnEnter unmountOnExit>
            {TAB_DEFINITIONS.map((definition) => (
              <Tab key={definition.key} eventKey={definition.key} title={<TabTitle definition={definition} count={counts[definition.key]} />}>
                {/* Remount the tab content when the filters change so each tab's pagination resets to page 1. */}
                <div className='d-flex flex-column gap-3' key={JSON.stringify(filters)}>
                  <div className='paragraph-small zocks-text-gray-600'>{definition.subtitle}</div>
                  {definition.content(filters)}
                </div>
              </Tab>
            ))}
          </Tabs>
        </Col>
      </Row>
    </Container>
  )
}
